// GraphQL BFF adapter. POSTs GraphQL documents to a partner gateway (e.g.
// tc-gateway-service). Falls back to REST-style request() for non-GraphQL paths.

import type { AuthAdapter } from '../auth/AuthAdapter';
import { BffError, type BffAdapter, type BffRequestOptions } from './BffAdapter';
import { generateTraceparent } from '../../common/utils/traceparent';
import { createFetchSseClient, type SseSubscription } from '../../common/services/fetchSse';

export interface GraphqlBffConfig {
  baseUrl: string;
  tenant?: string;
  tenantHeader?: string;
  authHeader?: string;
  authScheme?: string;
  defaultHeaders?: Record<string, string>;
}

interface GraphqlResponse<T> {
  data?: T;
  errors?: unknown[];
}

export class GraphqlBffAdapter implements BffAdapter {
  readonly name = 'graphql';
  private readonly cfg: GraphqlBffConfig;
  private readonly auth: AuthAdapter;

  constructor(config: GraphqlBffConfig, authAdapter: AuthAdapter) {
    this.cfg = config;
    this.auth = authAdapter;
  }

  private buildUrl(path?: string): string {
    const base = this.cfg.baseUrl.replace(/\/$/, '');
    if (!path) return base;
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }

  private buildAuthHeader(token: string): string {
    const scheme = this.cfg.authScheme ?? 'Bearer';
    return scheme ? `${scheme} ${token}` : token;
  }

  private authHeaders(): Record<string, string> {
    const token = this.auth.getAccessToken();
    const tenantHeader = this.cfg.tenantHeader ?? 'X-Tenant';
    const authHeader = this.cfg.authHeader ?? 'Authorization';

    return {
      'Content-Type': 'application/json',
      ...(token ? { [authHeader]: this.buildAuthHeader(token) } : {}),
      ...(this.cfg.tenant ? { [tenantHeader]: this.cfg.tenant } : {}),
      ...this.cfg.defaultHeaders,
      traceparent: generateTraceparent(),
    };
  }

  async request<T = unknown>(path: string, options: BffRequestOptions = {}): Promise<T> {
    const res = await fetch(this.buildUrl(path), {
      method: options.method ?? 'GET',
      headers: { ...this.authHeaders(), ...options.headers },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });

    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      throw new BffError(res.status, res.statusText, body);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  async query<T>(document: string, variables?: Record<string, unknown>): Promise<T> {
    const res = await fetch(this.buildUrl(), {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ query: document, variables }),
    });

    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      throw new BffError(res.status, res.statusText, body);
    }

    const payload = (await res.json()) as GraphqlResponse<T>;
    if (payload.errors?.length) {
      throw new BffError(400, 'GraphQL Error', payload.errors);
    }
    return payload.data as T;
  }

  openEventStream(path: string): SseSubscription {
    return createFetchSseClient(this.buildUrl(path), {
      getBearer: async () => this.auth.getAccessToken() ?? '',
    });
  }
}
